import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { mutualisteAPI, parameterAPI, enterpriseAPI } from '../../services/api';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';

const EditBeneficiaryModal = ({ beneficiary, isOpen, onClose, onSuccess }) => {
  // Récupérer les options de paramétrage
  const { data: parametersData, isLoading: parametersLoading, error: parametersError } = useQuery(
    'parameters-options-v3',
    () => parameterAPI.getActiveOptions(),
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: true,
      onError: (error) => {
        console.error('❌ [EditBeneficiary] Erreur récupération options:', error);
      }
    }
  );

  // Options d'entreprises actives pour la liste déroulante
  const { data: enterprisesOptionsData, isLoading: enterprisesLoading, error: enterprisesError } = useQuery(
    'enterprises-options-for-beneficiary-edit',
    () => enterpriseAPI.getOptions(),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      onError: (error) => {
        console.error('❌ [EditBeneficiary] Erreur récupération entreprises:', error);
      }
    }
  );

  const [formData, setFormData] = useState({
    // Informations personnelles
    nom: '',
    prenoms: '',
    genre: '',
    dateNaissance: '',
    lieuNaissance: '',
    nationalite: '',
    situationMatrimoniale: '',
    
    // Informations professionnelles (pour adhérents)
    entreprise: '',
    fonction: '',
    emailProfessionnel: '',
    telephoneProfessionnel: '',
    adresseProfessionnelle: '',
    
    // Informations de résidence
    adresseResidence: '',
    ville: '',
    codePostal: '',
    pays: '',
    
    // Contact
    mobile1: '',
    mobile2: '',
    emailPersonnel: '',
    adressePostale: '',
    
    // Pour ayants-droit
    lienParente: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialiser le formulaire avec les données du bénéficiaire
  useEffect(() => {
    if (beneficiary && isOpen) {
      setFormData({
        nom: beneficiary.nom || '',
        prenoms: beneficiary.prenoms || '',
        genre: beneficiary.genre?.id || beneficiary.genre || '',
        dateNaissance: beneficiary.dateNaissance ? beneficiary.dateNaissance.split('T')[0] : '',
        lieuNaissance: beneficiary.lieuNaissance || '',
        nationalite: beneficiary.nationalite || '',
        situationMatrimoniale: beneficiary.situationMatrimoniale?.id || beneficiary.situationMatrimoniale || '',
        
        entreprise: beneficiary.enterprise?.id || beneficiary.entreprise || '',
        fonction: beneficiary.fonction?.id || beneficiary.fonction || '',
        emailProfessionnel: beneficiary.emailProfessionnel || '',
        telephoneProfessionnel: beneficiary.telephoneProfessionnel || '',
        adresseProfessionnelle: beneficiary.adresseProfessionnelle || '',
        
        adresseResidence: beneficiary.adresseResidence || '',
        ville: beneficiary.ville || '',
        codePostal: beneficiary.codePostal || '',
        pays: beneficiary.pays || "Côte d'Ivoire",
        
        mobile1: beneficiary.mobile1 || '',
        mobile2: beneficiary.mobile2 || '',
        emailPersonnel: beneficiary.emailPersonnel || '',
        adressePostale: beneficiary.adressePostale || '',
        
        // Pour les ayants-droit, on stocke l'ID du lien de parenté
        lienParente: beneficiary.lienParente?.id || beneficiary.lienParente || ''
      });
      setErrors({});
    }
  }, [beneficiary, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Champs obligatoires
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est obligatoire';
    if (!formData.prenoms.trim()) newErrors.prenoms = 'Les prénoms sont obligatoires';
    if (!formData.genre) newErrors.genre = 'Le genre est obligatoire';
    if (!formData.dateNaissance) newErrors.dateNaissance = 'La date de naissance est obligatoire';
    if (!formData.adresseResidence.trim()) newErrors.adresseResidence = 'L\'adresse de résidence est obligatoire';
    if (!formData.ville.trim()) newErrors.ville = 'La ville est obligatoire';
    if (!formData.mobile1.trim()) newErrors.mobile1 = 'Le mobile principal est obligatoire';

    // Validation email
    if (formData.emailPersonnel && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailPersonnel)) {
      newErrors.emailPersonnel = 'Format d\'email invalide';
    }
    if (formData.emailProfessionnel && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailProfessionnel)) {
      newErrors.emailProfessionnel = 'Format d\'email invalide';
    }

    // Validation téléphone (format simple)
    if (formData.mobile1 && !/^[0-9+\-\s()]{8,}$/.test(formData.mobile1)) {
      newErrors.mobile1 = 'Format de téléphone invalide';
    }
    if (formData.mobile2 && !/^[0-9+\-\s()]{8,}$/.test(formData.mobile2)) {
      newErrors.mobile2 = 'Format de téléphone invalide';
    }

    // Pour les ayants-droit, le lien de parenté est obligatoire
    if (beneficiary?.type === 'AYANT_DROIT' && !formData.lienParente.trim()) {
      newErrors.lienParente = 'Le lien de parenté est obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setIsLoading(true);
    
    try {
      // Préparer les données pour l'API
      const updateData = {
        ...formData,
        dateNaissance: new Date(formData.dateNaissance).toISOString()
      };

      // Supprimer les champs vides pour éviter d'écraser avec des valeurs nulles
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '') {
          delete updateData[key];
        }
      });

      await mutualisteAPI.updateMutualiste(beneficiary.id, updateData);
      
      toast.success('Bénéficiaire modifié avec succès');
      onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la modification');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !beneficiary) return null;

  const isAdherent = beneficiary.type === 'ADHERENT';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              Modifier {isAdherent ? 'l\'adhérent' : 'l\'ayant-droit'}
            </h3>
            <p className="text-gray-600 mt-1">
              {beneficiary.prenoms} {beneficiary.nom} ({beneficiary.matricule})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informations personnelles */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Informations personnelles</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.nom ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénoms *
                </label>
                <input
                  type="text"
                  name="prenoms"
                  value={formData.prenoms}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.prenoms ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.prenoms && <p className="text-red-500 text-xs mt-1">{errors.prenoms}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Genre *
                </label>
                <select
                  name="genre"
                  value={formData.genre}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.genre ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={parametersLoading}
                >
                  <option value="">
                    {parametersLoading ? 'Chargement...' : 
                     parametersError ? 'Erreur de chargement' :
                     parametersData?.data?.data?.genres?.length ? 'Sélectionner' : 'Aucune donnée'}
                  </option>
                  {parametersData?.data?.data?.genres?.map((genre) => (
                    <option key={genre.id} value={genre.id}>
                      {genre.libelle}
                    </option>
                  ))}
                </select>
                {errors.genre && <p className="text-red-500 text-xs mt-1">{errors.genre}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de naissance *
                </label>
                <input
                  type="date"
                  name="dateNaissance"
                  value={formData.dateNaissance}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.dateNaissance ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dateNaissance && <p className="text-red-500 text-xs mt-1">{errors.dateNaissance}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lieu de naissance
                </label>
                <input
                  type="text"
                  name="lieuNaissance"
                  value={formData.lieuNaissance}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nationalité
                </label>
                <input
                  type="text"
                  name="nationalite"
                  value={formData.nationalite}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {isAdherent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Situation matrimoniale
                  </label>
                  <select
                    name="situationMatrimoniale"
                    value={formData.situationMatrimoniale}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={parametersLoading}
                  >
                    <option value="">
                      {(() => {
                        if (parametersLoading) return 'Chargement...';
                        if (parametersError) return 'Erreur de chargement';
                        return parametersData?.data?.data?.situationMatrimoniale?.length ? 'Sélectionner' : 'Aucune donnée';
                      })()}
                    </option>
                    {parametersData?.data?.data?.situationMatrimoniale?.map((situation) => (
                      <option key={situation.id} value={situation.id}>
                        {situation.libelle}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {!isAdherent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lien de parenté *
                  </label>
                  <select
                    name="lienParente"
                    value={formData.lienParente}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.lienParente ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={parametersLoading}
                  >
                    <option value="">
                      {parametersLoading ? 'Chargement...' : 
                       parametersError ? 'Erreur de chargement' :
                       parametersData?.data?.data?.lienParente?.length ? 'Sélectionner' : 'Aucune donnée'}
                    </option>
                    {parametersData?.data?.data?.lienParente?.map((lien) => (
                      <option key={lien.id} value={lien.id}>
                        {lien.libelle}
                      </option>
                    ))}
                  </select>
                  {errors.lienParente && <p className="text-red-500 text-xs mt-1">{errors.lienParente}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Informations professionnelles (pour adhérents) */}
          {isAdherent && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Informations professionnelles</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entreprise
                  </label>
                  <select
                    name="entreprise"
                    value={formData.entreprise}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={enterprisesLoading}
                  >
                    <option value="">
                      {enterprisesLoading
                        ? 'Chargement...'
                        : enterprisesError
                          ? 'Erreur de chargement'
                          : enterprisesOptionsData?.data?.data?.length
                            ? 'Sélectionner'
                            : 'Aucune donnée'}
                    </option>
                    {enterprisesOptionsData?.data?.data?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fonction
                  </label>
                  <select
                    name="fonction"
                    value={formData.fonction}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={parametersLoading}
                  >
                    <option value="">
                      {parametersLoading
                        ? 'Chargement...'
                        : parametersError
                          ? 'Erreur de chargement'
                          : parametersData?.data?.data?.fonctions?.length
                            ? 'Sélectionner'
                            : 'Aucune donnée'}
                    </option>
                    {parametersData?.data?.data?.fonctions?.map((fonction) => (
                      <option key={fonction.id} value={fonction.id}>
                        {fonction.libelle}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email professionnel
                  </label>
                  <input
                    type="email"
                    name="emailProfessionnel"
                    value={formData.emailProfessionnel}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.emailProfessionnel ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.emailProfessionnel && <p className="text-red-500 text-xs mt-1">{errors.emailProfessionnel}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone professionnel
                  </label>
                  <input
                    type="tel"
                    name="telephoneProfessionnel"
                    value={formData.telephoneProfessionnel}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse professionnelle
                  </label>
                  <textarea
                    name="adresseProfessionnelle"
                    value={formData.adresseProfessionnelle}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Informations de résidence */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Adresse de résidence</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse de résidence *
                </label>
                <textarea
                  name="adresseResidence"
                  value={formData.adresseResidence}
                  onChange={handleInputChange}
                  rows={2}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.adresseResidence ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.adresseResidence && <p className="text-red-500 text-xs mt-1">{errors.adresseResidence}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ville *
                </label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.ville ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.ville && <p className="text-red-500 text-xs mt-1">{errors.ville}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code postal
                </label>
                <input
                  type="text"
                  name="codePostal"
                  value={formData.codePostal}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pays
                </label>
                <input
                  type="text"
                  name="pays"
                  value={formData.pays}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Informations de contact */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile principal *
                </label>
                <input
                  type="tel"
                  name="mobile1"
                  value={formData.mobile1}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.mobile1 ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.mobile1 && <p className="text-red-500 text-xs mt-1">{errors.mobile1}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile secondaire
                </label>
                <input
                  type="tel"
                  name="mobile2"
                  value={formData.mobile2}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.mobile2 ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.mobile2 && <p className="text-red-500 text-xs mt-1">{errors.mobile2}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email personnel
                </label>
                <input
                  type="email"
                  name="emailPersonnel"
                  value={formData.emailPersonnel}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.emailPersonnel ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.emailPersonnel && <p className="text-red-500 text-xs mt-1">{errors.emailPersonnel}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse postale
                </label>
                <input
                  type="text"
                  name="adressePostale"
                  value={formData.adressePostale}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBeneficiaryModal;
